import { Component, createRef } from 'react';
import type { ReactElement, RefObject } from 'react';

import { GLOBAL } from 'saddlebag-browser';

import type { Nullable } from '@shared/types/utils';

type Props = {
  threshold?: number;
  onElementSeen: Function;
  children: ReactElement;
  ioWrapperClassName?: string;
  enabled?: boolean;
};

type Entries = Array<
  IntersectionObserverEntry & {
    isVisible?: boolean;
  }
>;

const DEFAULT_THRESHOLD = 0.5;

const defaultProps = {
  enabled: true,
};

class IntersectionObserverWrapper extends Component<Props> {
  static defaultProps = defaultProps;

  ref: RefObject<HTMLDivElement>;

  observer: IntersectionObserver;

  haveObserved: boolean;

  constructor(props: Props) {
    super(props);

    this.ref = createRef();
    this.observer = this.getObserver();
    this.haveObserved = false;
  }

  componentDidMount() {
    if (this.props.enabled) {
      this.addObserve();
    }
  }

  componentDidUpdate() {
    if (this.props.enabled && !this.haveObserved) {
      this.addObserve();
    }
  }

  componentWillUnmount() {
    if (this.props.enabled) {
      this.removeObserve();
    }
  }

  addObserve = () => {
    if (this.ref.current && this.observer) {
      this.observer.observe(this.ref.current);
      this.haveObserved = true;
    }
  };

  removeObserve = () => {
    if (this.ref.current && this.observer) {
      this.observer.unobserve(this.ref.current);
      this.haveObserved = false;
    }
  };

  getObserver: () => IntersectionObserver = () => {
    const $window = GLOBAL.getWindow();
    if (!$window || typeof $window.IntersectionObserver !== 'function') {
      return null;
    }

    const delayTime = 500;
    const observerOptions = {
      threshold: this.props.threshold || DEFAULT_THRESHOLD,
      delay: delayTime,
      trackVisibility: true,
      root: null,
    };

    let observeTimeout: Nullable<ReturnType<typeof setTimeout>>;
    return new $window.IntersectionObserver((entries: Entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= (this.props.threshold || DEFAULT_THRESHOLD)
        ) {
          if (entry.isVisible) {
            this.props.onElementSeen();
          } else {
            // The browser doesn't support Intersection Observer v2(entry.isVisible is undefined), falling back to v1 behavior.
            observeTimeout = setTimeout(
              () => this.props.onElementSeen(),
              delayTime,
            );
          }
        }

        if (!entry.isIntersecting && observeTimeout) {
          clearTimeout(observeTimeout);
        }
      });
    }, observerOptions);
  };

  render() {
    const { children, ioWrapperClassName } = this.props;
    return (
      <div ref={this.ref} className={ioWrapperClassName}>
        {children}
      </div>
    );
  }
}

export default IntersectionObserverWrapper;
